import { ReloadIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Grid, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { GlobalContext } from 'contexts/global.context';
import { TABLES } from 'enums/tables';
import { TableIdentifier } from 'interfaces/cookies/i-app.cookie';
import { ITableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IBallTypeConfig } from 'lib_ts/interfaces/i-ball-types';
import { useEffect, useMemo } from 'react';
import useBallTypesStore from './store/use-ball-types-store';

const IDENTIFIER = TableIdentifier.AdminBallTypeList;

const PAGE_SIZES = TABLES.PAGE_SIZES.SM;

export const BallTypesTable = () => {
  const {
    configs,
    loading,
    refLists,
    fetchConfigs,
    fetchRefLists,
    updateConfig,
  } = useBallTypesStore();

  const calibrationOptions = useMemo<IOption[]>(
    () =>
      refLists
        .filter(
          (l) => l.reference_type === ReferenceListType.BallTypeCalibration
        )
        .map((l) => ({ label: l.name, value: l._id })),
    [refLists]
  );

  const repeatabilityOptions = useMemo<IOption[]>(
    () =>
      refLists
        .filter((l) => l.reference_type === ReferenceListType.Repeatability)
        .map((l) => ({ label: l.name, value: l._id })),
    [refLists]
  );

  const columns: ITableColumn[] = [
    {
      label: 'common.ball-type',
      key: 'ball_type',
      classNameFn: () => 'v-align-top',
      formatFn: (a: IBallTypeConfig) => {
        const color =
          a.calibrationListID && a.repeatabilityListID
            ? undefined
            : RADIX.COLOR.DANGER;

        return (
          <Box pt="3" pb="3">
            <Text color={color}>{a.ball_type}</Text>
          </Box>
        );
      },
    },
    {
      label: 'Ball Type Calibration List',
      key: 'calibrationListID',
      classNameFn: () => 'v-align-top',
      formatFn: (a: IBallTypeConfig) => (
        <Box pt="3" pb="3">
          <CommonSearchInput
            id={`${a._id}-btc`}
            values={a.calibrationListID ? [a.calibrationListID] : []}
            onChange={(values) =>
              updateConfig(a._id, { calibrationListID: values[0] })
            }
            options={calibrationOptions}
            required
          />
        </Box>
      ),
      disableSort: true,
    },
    {
      label: 'Repeatability Testing List',
      key: 'repeatabilityListID',
      classNameFn: () => 'v-align-top',
      formatFn: (a: IBallTypeConfig) => (
        <Box pt="3" pb="3">
          <CommonSearchInput
            id={`${a._id}-repeatability`}
            values={a.repeatabilityListID ? [a.repeatabilityListID] : []}
            onChange={(values) =>
              updateConfig(a._id, { repeatabilityListID: values[0] })
            }
            options={repeatabilityOptions}
            required
          />
        </Box>
      ),
      disableSort: true,
    },
  ];

  useEffect(() => {
    fetchConfigs();
    fetchRefLists();
  }, []);

  const pagination: ITablePageable = {
    identifier: IDENTIFIER,
    total: configs.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'ball_type',
      dir: -1,
    },
  };

  return (
    <ErrorBoundary componentName="BallTypesTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <GlobalContext.Consumer>
            {(globalCx) => (
              <CommonTableHoC
                id="AdminBallTypes"
                toolbarContent={
                  <Flex gap={RADIX.FLEX.GAP.SM}>
                    <Grid flexGrow="1" columns="5" gap={RADIX.FLEX.GAP.SM}>
                      <Box>
                        <Button onClick={() => fetchConfigs()}>
                          <ReloadIcon /> Refresh
                        </Button>
                      </Box>
                    </Grid>
                  </Flex>
                }
                displayColumns={columns}
                displayData={configs}
                loading={loading}
                enableListener={globalCx.dialogs.length === 0}
                {...pagination}
                {...sort}
                vFlex
              />
            )}
          </GlobalContext.Consumer>
        }
      />
    </ErrorBoundary>
  );
};
